'use client';

import React from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Box, Typography } from '@mui/material';
import type { BaseModalProps } from '@/interfaces/component/modal.interface';
import { RequireAuthModalContent, RequireAuthModalProps, RequireAuthType } from '@/interfaces/component/requireAuth.interface';
import { ROUTES } from '@/constants/path.constants';
import BaseModal from '@/components/modal/BaseModal';

const imageConfig: Record<RequireAuthType, { src: string; aspectRatio: string; maxWidth?: string }> = {
  delete: {
    src: '/images/mockup/delete_requireAuth.png',
    aspectRatio: '232/198'
  },
  punish: {
    src: '/images/mockup/punish_requireAuth.png',
    aspectRatio: '731/587'
  },
  aiRecommendReply: {
    src: '/images/mockup/aiRecommendReply_requireAuth.png',
    aspectRatio: '443/336',
    maxWidth: '300px'
  },
  showMore: {
    src: '/images/mockup/iphone_mockup_modal_requireAuth.png',
    aspectRatio: '680/351',
    maxWidth: '300px'
  }
};

const getAuthMessage = (type: RequireAuthType) => {
  const messages: Record<RequireAuthType, RequireAuthModalContent> = {
    delete: {
      title: '악성 리뷰를 손쉽고 빠르게 삭제하세요.',
      content: (
        <>
          30초면 충분합니다.
          <br />
          더 많은 악성 리뷰를 확인하고
          <br />
          처리는 리뷰웍스에게 맡겨주세요.
          <br />
        </>
      )
    },
    punish: {
      title: '더 많은 리뷰를 확인하고, 부정적인 댓글에 대해 관리를 요청할 수 있습니다.',
      content: (
        <>
          악성으로 판단된 댓글의 경우, 논의 후 가장 적절한 해결 방안을 제공해 드립니다.
          <br />
          ※ 본 기능은 엔터프라이즈 요금제 이용 시 제공됩니다.
          <br />
          <br />
          30초면 가입 완료, 지금 시작해보세요.
        </>
      )
    },
    aiRecommendReply: {
      title: 'AI가 환자의 리뷰를 분석하여 친절한 답변을 작성해드립니다.',
      content: (
        <>
          30초면 충분합니다. 지금 가입하고
          <br />
          <strong style={{ color: '#4CAF50', fontWeight: 'bold' }}>무료</strong>로 여러 플랫폼의 리뷰를 한번에 관리하세요.
        </>
      )
    },
    showMore: {
      title: '우리 병원에 대한 더 많은 리뷰가 있습니다.',
      content: (
        <>
          30초면 충분합니다. 지금 가입하고
          <br />
          <strong style={{ color: '#4CAF50', fontWeight: 'bold' }}>무료</strong>로 편리한 리뷰 관리 기능을 이용하세요.
        </>
      )
    }
  };

  return (
    <Box sx={{ textAlign: 'left', mb: '8px' }}>
      <Typography variant="subtitle1" component="strong">
        {messages[type].title}
      </Typography>
      <Typography sx={{ my: '14px', fontSize: '1rem' }}>{messages[type].content}</Typography>
      {messages[type].extra && <Typography sx={{ color: 'gray', fontSize: '1rem' }}>{messages[type].extra}</Typography>}
    </Box>
  );
};

const RequestAuthenticationModal = (props: RequireAuthModalProps) => {
  const router = useRouter();
  const { showModal, handleShowModal, type } = props;

  const modalProps: BaseModalProps = {
    showModal,
    modalTitle: '회원 가입이 필요합니다.',
    onCloseModal: handleShowModal,
    handleShowModal,
    buttons: {
      primary: {
        title: '회원가입',
        onClick: () => router.push(ROUTES.getSignUp())
      },
      secondary: {
        title: '로그인',
        onClick: () => router.push(ROUTES.getLogin())
      }
    }
  };

  return (
    <BaseModal {...modalProps}>
      <Box sx={{ paddingX: 0 }}>
        <Typography color="textSecondary" sx={{ paddingX: 0 }}>
          {getAuthMessage(type)}
        </Typography>
        {type !== 'punish' && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: imageConfig[type].aspectRatio,
              margin: '16px 0',
              maxWidth: imageConfig[type].maxWidth
            }}
          >
            <Image src={imageConfig[type].src} alt="리뷰 미리보기" fill style={{ objectFit: 'contain' }} />
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};

export default RequestAuthenticationModal;
